import { databasesStardogRequestDispatchers } from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { PublishProjectResponse } from 'src/actions/projects/actionCreators';
import { getRuleModel } from 'src/containers/ProjectDiagram/utils/rules/getRuleModel';
import {
  ModelInfo,
  RulesInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { Database } from 'src/store/state/databases';
import { Project } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { exportRules } from 'src/utils/export/rules';

export const publishRules = async ({
  database,
  project,
  modelInfo,
  ruleInfo,
}: {
  database: Database;
  project: Project;
  modelInfo: ModelInfo;
  ruleInfo: RulesInfo;
}): Promise<PublishProjectResponse | null> => {
  if (!Object.keys(ruleInfo).length || !project.diagram) {
    return null;
  }

  // Publish the rules
  const ruleModel = getRuleModel(project.diagram.model);
  const rulesFile = exportRules(
    project.name,
    ruleModel,
    ruleInfo,
    database.namespaces || {}
  );

  if (!rulesFile) {
    return null;
  }

  const databaseId = database.id;

  // Begin a transaction
  const transactionResponse =
    await databasesStardogRequestDispatchers.beginDatabaseTransaction(
      databaseId
    );

  const transactionId = transactionResponse.payload.response?.body;

  if (
    transactionResponse.type ===
      DatabasesActionType.BEGIN_DATABASE_TRANSACTION_FAILURE ||
    !transactionId
  ) {
    return {
      success: false,
      action: transactionResponse,
      actionDescription: copy.notifications.database.beginTransaction,
      message: copy.notifications.publishProject.rules.failure,
    };
  }

  // Prep to rollback if we need to
  const rollback = async () => {
    await databasesStardogRequestDispatchers.rollbackDatabaseTransaction(
      databaseId,
      transactionId
    );
  };

  // Publish the rules
  const addDataResponse =
    await databasesStardogRequestDispatchers.addDataToDatabase({
      databaseId,
      transactionId,
      contents: rulesFile.contents,
      mimeType: rulesFile.mimeType,
      namedGraph: modelInfo.namedGraphIri,
    });

  if (
    addDataResponse.type === DatabasesActionType.ADD_DATA_TO_DATABASE_FAILURE
  ) {
    // Stardog doesn't actually start a transaction until an action
    // has succeeded, so no need to rollback the first one if it fails
    return {
      success: false,
      action: addDataResponse,
      actionDescription: copy.notifications.database.addData,
      message: copy.notifications.publishProject.rules.failure,
    };
  }

  // Commit the transaction
  const commitResponse =
    await databasesStardogRequestDispatchers.commitDatabaseTransaction(
      databaseId,
      transactionId
    );

  if (
    commitResponse.type ===
    DatabasesActionType.COMMIT_DATABASE_TRANSACTION_FAILURE
  ) {
    await rollback();
    return {
      success: false,
      action: commitResponse,
      actionDescription: copy.notifications.database.commitTransaction,
      message: copy.notifications.publishProject.rules.failure,
    };
  }

  return {
    success: true,
    action: commitResponse,
    message: copy.notifications.publishProject.rules.success,
  };
};
